import { pathColor } from './shortest-paths.js';
import { segmentDistance } from './mobile-graph.js';

export function curveGeometry(a, b, lane = 0) {
  const dx = b.x - a.x, dy = b.y - a.y;
  const bend = .2 + lane * .12;
  return { a, b, control: { x: (a.x + b.x) / 2 - dy * bend, y: (a.y + b.y) / 2 + dx * bend } };
}
export function curvePoint(curve, t) {
  const { a, b, control: c } = curve, u = 1 - t;
  return { x: u * u * a.x + 2 * u * t * c.x + t * t * b.x,
    y: u * u * a.y + 2 * u * t * c.y + t * t * b.y };
}
export function curveTangent(curve, t) {
  const { a, b, control: c } = curve;
  return { x: 2 * ((1 - t) * (c.x - a.x) + t * (b.x - c.x)),
    y: 2 * ((1 - t) * (c.y - a.y) + t * (b.y - c.y)) };
}
export function nearestCurve(point, curves, tolerance = 7) {
  let best = null, distance = tolerance;
  // Foreground wins a close crossing, not an invisible straight chord.
  for (const curve of curves) {
    const pad = tolerance;
    if (point.x < Math.min(curve.a.x, curve.b.x, curve.control.x) - pad || point.x > Math.max(curve.a.x, curve.b.x, curve.control.x) + pad
      || point.y < Math.min(curve.a.y, curve.b.y, curve.control.y) - pad || point.y > Math.max(curve.a.y, curve.b.y, curve.control.y) + pad) continue;
    let from = curvePoint(curve, curve.start);
    const steps = Math.min(256, Math.max(16, Math.ceil(Math.hypot(curve.b.x - curve.a.x, curve.b.y - curve.a.y) / 12)));
    let d = Infinity;
    for (let i = 1; i <= steps; i++) {
      const to = curvePoint(curve, curve.start + (curve.end - curve.start) * i / steps);
      d = Math.min(d, segmentDistance(point, from, to)); from = to;
    }
    if (d < tolerance && ((!best || Boolean(curve.mask) > Boolean(best.mask)) || Boolean(curve.mask) === Boolean(best.mask) && d < distance)) {
      best = curve; distance = d;
    }
  }
  return best;
}

// Separate canvas beneath Sigma's node layer. Main-view rendering is untouched.
export function installPathCurves(renderer, graph) {
  const canvas = document.createElement('canvas'); canvas.className = 'path-curves'; canvas.setAttribute('aria-hidden', 'true');
  const nodeCanvas = renderer.getCanvases().nodes;
  nodeCanvas.parentNode.insertBefore(canvas, nodeCanvas);
  const context = canvas.getContext('2d');
  return {
    draw(paths, mobile) {
      const { width, height } = renderer.getDimensions(), dpr = window.devicePixelRatio || 1;
      if (canvas.width !== Math.round(width * dpr) || canvas.height !== Math.round(height * dpr)) {
        canvas.width = Math.round(width * dpr); canvas.height = Math.round(height * dpr);
      }
      context.setTransform(dpr, 0, 0, dpr, 0, 0); context.clearRect(0, 0, width, height);
      canvas.hidden = !paths;
      if (!paths) return [];
      const points = new Map(graph.mapNodes((id, data) => [id, { ...renderer.graphToViewport(data), size: renderer.scaleSize(renderer.getNodeDisplayData(id).size) }]));
      const lanes = new Map(), curves = [];
      // Canonical endpoints make parallel and opposing factual edges stable.
      for (const id of graph.edges().sort()) {
        const [source, target] = graph.extremities(id).sort(), key = `${source}:${target}`;
        const lane = lanes.get(key) || 0; lanes.set(key, lane + 1);
        const a = points.get(source), b = points.get(target), mask = paths.edges.get(id) || 0;
        const curve = { id, source, target, mask, ...curveGeometry(a, b, lane) };
        const length = Math.hypot(b.x - a.x, b.y - a.y);
        if (length < 1) continue;
        curve.start = Math.min(.4, (a.size + 1) / length); curve.end = 1 - Math.min(.4, (b.size + 1) / length);
        curves.push(curve);
      }
      curves.sort((a, b) => Number(Boolean(a.mask)) - Number(Boolean(b.mask)));
      for (const curve of curves) {
        const start = curvePoint(curve, curve.start), end = curvePoint(curve, curve.end), tangent = curveTangent(curve, curve.start);
        const control = { x: start.x + tangent.x * (curve.end - curve.start) / 2, y: start.y + tangent.y * (curve.end - curve.start) / 2 };
        context.strokeStyle = curve.mask ? pathColor(curve.mask) : 'rgba(88,99,95,0.18)';
        context.lineWidth = curve.mask ? (mobile ? 2 : 2.5) : (mobile ? .45 : .7);
        context.beginPath(); context.moveTo(start.x, start.y); context.quadraticCurveTo(control.x, control.y, end.x, end.y); context.stroke();
      }
      return curves;
    },
    kill() { canvas.remove(); },
  };
}
